import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { RepositoryFactory } from "../../../repositories";
import TableContainer from "../../../components/Common/DataTableContainer";
import { pb } from "../../../helpers/pocketbase_helper";
import { generateExcel } from "../../../helpers/inventarioMaterialesExcelHelper";

const InventarioMaterialesPage = () => {
    document.title = "Inventario de Materiales | GraphicForce - Printesco";

    const [materiales, setMateriales] = useState([]);
    const [materialesInactivos, setMaterialesInactivos] = useState([]);
    const [inactPageIndex, setInactPageIndex] = useState(0);
    const [inactPageSize, setInactPageSize] = useState(10);
    const [inactTotalRecords, setInactTotalRecords] = useState(0);
    const [materialesInactivosPage, setMaterialesInactivosPage] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [modalAgregarCantidad, setModalAgregarCantidad] = useState(false);
    const [modalRestarCantidad, setModalRestarCantidad] = useState(false);
    const [form, setForm] = useState({ nombre: "", total: "", unidad: "" });
    const [formCantidad, setFormCantidad] = useState({ cantidad: "" });
    const [materialSelected, setMaterialSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savingCantidad, setSavingCantidad] = useState(false);
    const [modalError, setModalError] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [exportSuccess, setExportSuccess] = useState("");

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isPagination] = useState(true);
    const [isCustomPageSize] = useState(true);

    // Estados para unidades (select)
    const [unidades, setUnidades] = useState([]);

    // Estado para movimientos
    const [modalMovimientos, setModalMovimientos] = useState(false);
    const [movimientos, setMovimientos] = useState([]);
    const [movTotalItems, setMovTotalItems] = useState(0);
    const [movPageIndex, setMovPageIndex] = useState(0);
    const [movPageSize, setMovPageSize] = useState(10);

    // Alert states for inactivate/restore (similar to tipos_solicitud)
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [materialToRestore, setMaterialToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Get repository instances
    const inventarioRepository = useMemo(() => RepositoryFactory.getRepository('inventario_materiales'), []);
    const unidadRepository = useMemo(() => RepositoryFactory.getRepository('unidad'), []);

    // Fetch unidades activas para el select
    useEffect(() => {
        const fetchUnidades = async () => {
            try {
                const unidadesActivas = await unidadRepository.getAllActive();
                setUnidades(unidadesActivas);
            } catch (err) {
                console.error("Error fetching unidades:", err);
            }
        };
        fetchUnidades();
    }, [unidadRepository]);

    // Fetch materiales (cargar todos, paginar solo activos al estilo sub_brillo)
    const fetchMateriales = async () => {
        try {
            const all = await inventarioRepository.getList(1, 1000, {
                expand: 'unidad',
                sort: '-created'
            });
            const activos = (all.items || []).filter(m => m.activo !== false);
            const inactivos = (all.items || []).filter(m => m.activo === false);

            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activosPaginados = activos.slice(startIndex, endIndex);

            setMateriales(activosPaginados);
            setTotalRecords(activos.length);
            setMaterialesInactivos(inactivos);
            setInactTotalRecords(inactivos.length);
            const inactStart = inactPageIndex * inactPageSize;
            const inactEnd = inactStart + inactPageSize;
            setMaterialesInactivosPage(inactivos.slice(inactStart, inactEnd));
        } catch (err) {
            console.error("Error fetching materiales:", err);
            setModalError("Error al cargar los materiales");
        }
    };


    useEffect(() => {
        fetchMateriales();
    }, [pageIndex, pageSize, inventarioRepository]);

    

    const reloadMateriales = () => {
        setPageIndex(0); // Reset to first page
        setInactPageIndex(0);
        fetchMateriales();
        
    };

    // Handler para exportar a Excel
    const handleExportarExcel = async () => {
        setExporting(true);
        setExportError("");
        setExportSuccess("");
        
        try {
            const materiales = await inventarioRepository.getMaterialesParaExportacion();
            
            if (!materiales || materiales.length === 0) {
                setExportError('No hay materiales para exportar');
                return;
            }
            
            generateExcel(materiales);
            setExportSuccess('Excel exportado correctamente');
            
            // Limpiar mensaje de éxito después de 3 segundos
            setTimeout(() => {
                setExportSuccess("");
            }, 3000);
        } catch (err) {
            console.error('Error al exportar:', err);
            setExportError('Error al exportar Excel: ' + (err.message || 'Error desconocido'));
        } finally {
            setExporting(false);
        }
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "", total: "", unidad: "" });
        setModalError("");
        setModalAdd(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value
        }));
        if (modalError) {
            setModalError("");
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            await inventarioRepository.createMaterial({
                nombre: form.nombre.trim().toUpperCase(),
                total: form.total ? parseFloat(form.total) : 0,
                unidad: form.unidad || undefined,
                activo: true,
            });
            setModalAdd(false);
            setForm({ nombre: "", total: "", unidad: "" });
            reloadMateriales();
        } catch (err) {
            console.error("Error creating material:", err);
            if (err?.data?.data?.nombre && err.data.data.nombre.code === "validation_not_unique") {
                setModalError(`El material con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`);
            } else {
                setModalError("Error al crear el material");
            }
        }
        setSaving(false);
    };

    // Get current user ID
    const getCurrentUserId = () => {
        const userId = pb.authStore.model?.id;
        if (!userId) {
            throw new Error('Usuario no autenticado');
        }
        return userId;
    };

    const handleAgregarCantidad = (material) => {
        setMaterialSelected(material);
        setFormCantidad({ cantidad: "" });
        setModalError("");
        setModalAgregarCantidad(true);
    };

    const handleRestarCantidad = (material) => {
        setMaterialSelected(material);
        setFormCantidad({ cantidad: "" });
        setModalError("");
        setModalRestarCantidad(true);
    };

    const handleChangeCantidad = (e) => {
        const { name, value } = e.target;
        setFormCantidad(prev => ({
            ...prev,
            [name]: value
        }));
        if (modalError) {
            setModalError("");
        }
    };

    const handleSaveAgregarCantidad = async (e) => {
        e.preventDefault();
        setSavingCantidad(true);
        setModalError("");

        const cantidad = parseFloat(formCantidad.cantidad);
        if (!cantidad || cantidad <= 0) {
            setModalError("La cantidad debe ser mayor a 0");
            setSavingCantidad(false);
            return;
        }

        try {
            const userId = getCurrentUserId();
            await inventarioRepository.agregarCantidad(materialSelected.id, cantidad, userId);
            setModalAgregarCantidad(false);
            setFormCantidad({ cantidad: "" });
            setMaterialSelected(null);
            reloadMateriales();
        } catch (err) {
            console.error("Error agregando cantidad:", err);
            setModalError("Error al agregar la cantidad");
        }
        setSavingCantidad(false);
    };

    const handleSaveRestarCantidad = async (e) => {
        e.preventDefault();
        setSavingCantidad(true);
        setModalError("");

        const cantidad = parseFloat(formCantidad.cantidad);
        if (!cantidad || cantidad <= 0) {
            setModalError("La cantidad debe ser mayor a 0");
            setSavingCantidad(false);
            return;
        }

        // Validate that quantity doesn't exceed available
        const currentTotal = materialSelected.total ?? 0;
        if (cantidad > currentTotal) {
            setModalError(`No hay suficiente cantidad disponible. Disponible: ${currentTotal}, Solicitado: ${cantidad}`);
            setSavingCantidad(false);
            return;
        }

        try {
            const userId = getCurrentUserId();
            await inventarioRepository.restarCantidad(materialSelected.id, cantidad, userId);
            setModalRestarCantidad(false);
            setFormCantidad({ cantidad: "" });
            setMaterialSelected(null);
            reloadMateriales();
        } catch (err) {
            console.error("Error restando cantidad:", err);
            setModalError("Error al restar la cantidad");
        }
        setSavingCantidad(false);
    };

    const handleVerMovimientos = async (material) => {
        setMaterialSelected(material);
        setMovimientos([]);
        setMovTotalItems(0);
        setMovPageIndex(0);
        setModalMovimientos(true);
        try {
            const result = await inventarioRepository.getMovimientosPorMaterial(material.id, 1, movPageSize);
            setMovimientos(result.items || []);
            setMovTotalItems(result.totalItems || 0);
        } catch (err) {
            console.error("Error cargando movimientos:", err);
        }
    };

    const handleRemove = (material) => {
        setMaterialToDelete(material);
        setShowDeleteAlert(true);
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!materialToDelete) return;
        setDeleting(true);
        try {
            await inventarioRepository.deactivate(materialToDelete.id);
            setShowDeleteAlert(false);
            setMaterialToDelete(null);
            setModalError("");
            reloadMateriales();
        } catch (err) {
            console.error('Error al inactivar material:', err);
            setModalError('No fue posible inactivar el material');
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setMaterialToDelete(null);
    };

    const handleRestore = (material) => {
        setMaterialToRestore(material);
        setShowRestoreAlert(true);
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRestore = async () => {
        if (!materialToRestore) return;
        setRestoring(true);
        try {
            await inventarioRepository.activate(materialToRestore.id);
            setShowRestoreAlert(false);
            setMaterialToRestore(null);
            setModalError("");
            reloadMateriales();
        } catch (err) {
            console.error('Error al activar material:', err);
            setModalError('No fue posible activar el material');
        }
        setRestoring(false);
    };

    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setMaterialToRestore(null);
    };

    // Recalcular paginación de inactivos cuando cambie la página/tamaño o el listado base
    useEffect(() => {
        if (!Array.isArray(materialesInactivos)) {
            setMaterialesInactivosPage([]);
            setInactTotalRecords(0);
            return;
        }
        const inactStart = inactPageIndex * inactPageSize;
        const inactEnd = inactStart + inactPageSize;
        setMaterialesInactivosPage(materialesInactivos.slice(inactStart, inactEnd));
        setInactTotalRecords(materialesInactivos.length);
    }, [materialesInactivos, inactPageIndex, inactPageSize]);

    useEffect(() => {
        const loadMovs = async () => {
            if (!modalMovimientos || !materialSelected) return;
            try {
                const result = await inventarioRepository.getMovimientosPorMaterial(materialSelected.id, movPageIndex + 1, movPageSize);
                setMovimientos(result.items || []);
                setMovTotalItems(result.totalItems || 0);
            } catch (err) {
                console.error("Error cargando movimientos:", err);
            }
        };
        loadMovs();
    }, [modalMovimientos, movPageIndex, movPageSize, materialSelected?.id, inventarioRepository]);

    // --- Columns Definition ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Total",
            accessorKey: "total",
            cell: info => {
                const total = info.getValue() ?? 0;
                return <span>{total}</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Unidad",
            accessorKey: "unidad",
            cell: info => {
                const material = info.row.original;
                const unidadNombre = material.expand?.unidad?.nombre || "Sin unidad";
                return <span>{unidadNombre}</span>;
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const material = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 260, maxWidth: 360 }}>
                        <Button color="success" size="sm" onClick={() => handleAgregarCantidad(material)}>
                            <i className="fas fa-plus me-1"></i> Agregar
                        </Button>
                        <Button color="warning" size="sm" onClick={() => handleRestarCantidad(material)}>
                            <i className="fas fa-minus me-1"></i> Restar
                        </Button>
                        <Button color="info" size="sm" onClick={() => handleVerMovimientos(material)}>
                            <i className="fas fa-list me-1"></i> Movimientos
                        </Button>
                        {typeof inventarioRepository?.deactivate === 'function' && (
                            <Button color="danger" size="sm" onClick={() => handleRemove(material)}>
                                <i className="mdi mdi-archive-arrow-down-outline me-1"></i> Inactivar
                            </Button>
                        )}
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [materiales]);

    const movimientoColumns = useMemo(() => [
        {
            header: "Fecha",
            id: "fecha",
            accessorFn: row => {
                const value = row.created;
                try {
                    return new Date(value).toLocaleString();
                } catch {
                    return value || "";
                }
            },
            cell: info => info.getValue(),
            enableSorting: false,
            enableColumnFilter: true,
        },
        {
            header: "Usuario",
            accessorKey: "expand.usuario.name",
            cell: info => {
                const row = info.row.original;
                return row.expand?.usuario?.name || row.expand?.usuario?.email || "-";
            },
            enableSorting: false,
        },
        {
            header: "Cantidad",
            accessorKey: "cantidad",
            enableSorting: false,
            enableColumnFilter: true,
            filterFn: (row, columnId, filterValue) => {
                const cellValue = row.getValue(columnId);
                if (filterValue == null || String(filterValue).trim() === "") return true;
                return String(cellValue ?? "").includes(String(filterValue).trim());
            },
        },
        {
            header: "Tipo",
            id: "tipo",
            accessorFn: row => (row.ingreso ? "Ingreso" : "Egreso"),
            cell: info => (
                info.getValue() === "Ingreso"
                    ? <span className="badge bg-success">Ingreso</span>
                    : <span className="badge bg-danger">Egreso</span>
            ),
            enableSorting: false,
            enableColumnFilter: true,
        },
    ], []);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Inventario Materiales" breadcrumbItem="Ingresos/Egresos" />

                    {/* Dismissing Alert for Inactivate */}
                    {showDeleteAlert && materialToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará el material <b>{materialToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && materialToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará el material <b>{materialToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Success Alert for Export */}
                    {exportSuccess && (
                        <Alert
                            color="success"
                            isOpen={!!exportSuccess}
                            toggle={() => setExportSuccess("")}
                            fade={true}
                        >
                            <strong>¡Éxito!</strong> {exportSuccess}
                        </Alert>
                    )}

                    {/* Error Alert for Export */}
                    {exportError && (
                        <Alert
                            color="danger"
                            isOpen={!!exportError}
                            toggle={() => setExportError("")}
                            fade={true}
                        >
                            <strong>Error:</strong> {exportError}
                        </Alert>
                    )}

                    <Row className="mb-4">
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Inventario de Materiales
                                        <div>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={handleExportarExcel}
                                                disabled={exporting}
                                                className="me-2"
                                            >
                                                {exporting ? (
                                                    <>
                                                        <Spinner size="sm" className="me-1" />
                                                        Exportando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-file-excel me-1"></i>
                                                        Exportar a Excel
                                                    </>
                                                )}
                                            </Button>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar Material
                                            </Button>
                                            {process.env.NODE_ENV === 'test' && materiales.length > 0 && (
                                                <>
                                                    <Button
                                                        data-testid="open-add-qty"
                                                        color="outline-success"
                                                        size="sm"
                                                        className="ms-2"
                                                        onClick={() => handleAgregarCantidad(materiales[0])}
                                                    >
                                                        Test: Agregar Cantidad
                                                    </Button>
                                                    <Button
                                                        data-testid="open-subtract-qty"
                                                        color="outline-warning"
                                                        size="sm"
                                                        className="ms-2"
                                                        onClick={() => handleRestarCantidad(materiales[0])}
                                                    >
                                                        Test: Restar Cantidad
                                                    </Button>
                                                    <Button
                                                        data-testid="open-movements"
                                                        color="outline-info"
                                                        size="sm"
                                                        className="ms-2"
                                                        onClick={() => handleVerMovimientos(materiales[0])}
                                                    >
                                                        Test: Movimientos
                                                    </Button>
                                                </>
                                            )}
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadMateriales}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={materiales}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {materialesInactivos.length > 0 && (
                        <Row className="mt-4">
                            <Col>
                                <Card>
                                    <CardBody>
                                        <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                            Materiales Inactivos
                                        </CardTitle>
                                        <div className="table-rep-plugin">
                                            <div className="table-responsive mb-0" data-pattern="priority-columns">
                                                <TableContainer
                                                    columns={[
                                                        { header: 'Nombre', accessorKey: 'nombre', enableColumnFilter: false },
                                                        { header: 'Total', accessorKey: 'total', enableColumnFilter: false },
                                                        {
                                                            header: 'Unidad',
                                                            accessorKey: 'unidad',
                                                            cell: info => {
                                                                const material = info.row.original;
                                                                const unidadNombre = material.expand?.unidad?.nombre || 'Sin unidad';
                                                                return <span>{unidadNombre}</span>;
                                                            },
                                                            enableSorting: false,
                                                            enableColumnFilter: false,
                                                        },
                                                        {
                                                            header: 'Acciones',
                                                            accessorKey: 'acciones',
                                                            cell: info => {
                                                                const row = info.row.original;
                                                                return (
                                                                    <div className="d-flex gap-2">
                                            <Button color="primary" size="sm" onClick={() => handleRestore(row)}>
                                                                            <i className="mdi mdi-archive-arrow-up-outline me-1"></i> Activar
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            },
                                                            enableSorting: false,
                                                            enableColumnFilter: false,
                                                            meta: { style: { width: '1%', whiteSpace: 'nowrap' } },
                                                        },
                                                    ]}
                                                    data={materialesInactivosPage}
                                                    isGlobalFilter={false}
                                                    isPagination={true}
                                                    isCustomPageSize={true}
                                                    pageIndex={inactPageIndex}
                                                    setPageIndex={setInactPageIndex}
                                                    pageSize={inactPageSize}
                                                    setPageSize={setInactPageSize}
                                                    totalRecords={inactTotalRecords}
                                                    pagination="pagination"
                                                    paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                    tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                                />
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            </div>

            {/* Modal Agregar Material */}
            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Material</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="total">Total Inicial</Label>
                            <Input
                                type="number"
                                name="total"
                                id="total"
                                value={form.total}
                                onChange={handleChange}
                                min="0"
                                step="0.01"
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="unidad">Unidad</Label>
                            <Input
                                type="select"
                                name="unidad"
                                id="unidad"
                                value={form.unidad}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione una unidad</option>
                                {unidades.map((unidad) => (
                                    <option key={unidad.id} value={unidad.id}>
                                        {unidad.nombre}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal Agregar Cantidad */}
            <Modal isOpen={modalAgregarCantidad} toggle={() => setModalAgregarCantidad(!modalAgregarCantidad)}>
                <ModalHeader toggle={() => setModalAgregarCantidad(!modalAgregarCantidad)}>
                    Agregar Cantidad - {materialSelected?.nombre}
                </ModalHeader>
                <Form onSubmit={handleSaveAgregarCantidad}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="cantidad">Cantidad a Agregar *</Label>
                            <Input
                                type="number"
                                name="cantidad"
                                id="cantidad"
                                value={formCantidad.cantidad}
                                onChange={handleChangeCantidad}
                                placeholder="Ingrese la cantidad a agregar"
                                step="0.01"
                                min="0.01"
                                required
                            />
                        </FormGroup>
                        {materialSelected && (
                            <div className="mt-3">
                                <p><strong>Total actual:</strong> {materialSelected.total ?? 0}</p>
                                {formCantidad.cantidad && !isNaN(parseFloat(formCantidad.cantidad)) && (
                                    <p><strong>Total después:</strong> {(materialSelected.total ?? 0) + parseFloat(formCantidad.cantidad)}</p>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAgregarCantidad(false)}>
                            Cancelar
                        </Button>
                        <Button color="success" type="submit" disabled={savingCantidad}>
                            {savingCantidad ? <Spinner size="sm" className="me-2" /> : null}
                            {savingCantidad ? "Guardando..." : "Agregar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal Restar Cantidad */}
            <Modal isOpen={modalRestarCantidad} toggle={() => setModalRestarCantidad(!modalRestarCantidad)}>
                <ModalHeader toggle={() => setModalRestarCantidad(!modalRestarCantidad)}>
                    Restar Cantidad - {materialSelected?.nombre}
                </ModalHeader>
                <Form onSubmit={handleSaveRestarCantidad}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="cantidad">Cantidad a Restar *</Label>
                            <Input
                                type="number"
                                name="cantidad"
                                id="cantidad"
                                value={formCantidad.cantidad}
                                onChange={handleChangeCantidad}
                                placeholder="Ingrese la cantidad a restar"
                                step="0.01"
                                min="0.01"
                                required
                            />
                        </FormGroup>
                        {materialSelected && (
                            <div className="mt-3">
                                <p><strong>Total actual:</strong> {materialSelected.total ?? 0}</p>
                                {formCantidad.cantidad && !isNaN(parseFloat(formCantidad.cantidad)) && (
                                    <p><strong>Total después:</strong> {(materialSelected.total ?? 0) - parseFloat(formCantidad.cantidad)}</p>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalRestarCantidad(false)}>
                            Cancelar
                        </Button>
                        <Button color="warning" type="submit" disabled={savingCantidad}>
                            {savingCantidad ? <Spinner size="sm" className="me-2" /> : null}
                            {savingCantidad ? "Guardando..." : "Restar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal Movimientos */}
            <Modal isOpen={modalMovimientos} toggle={() => setModalMovimientos(!modalMovimientos)} size="lg">
                <ModalHeader toggle={() => setModalMovimientos(!modalMovimientos)}>
                    Movimientos - {materialSelected?.nombre}
                </ModalHeader>
                <ModalBody>
                    <TableContainer
                        columns={movimientoColumns}
                        data={movimientos}
                        isGlobalFilter={false}
                        isPagination={true}
                        isCustomPageSize={true}
                        pageIndex={movPageIndex}
                        setPageIndex={setMovPageIndex}
                        pageSize={movPageSize}
                        setPageSize={setMovPageSize}
                        totalRecords={movTotalItems}
                        pagination="pagination"
                        paginationWrapper='dataTables_paginate paging_simple_numbers'
                        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModalMovimientos(false)}>
                        Cerrar
                    </Button>
                </ModalFooter>
            </Modal>

            
        </React.Fragment>
    );
};

export default InventarioMaterialesPage;

